/**
 * Reading a DCX featureId — the one place that knows how the segments decompose.
 *
 * The shapes, and the trap in each:
 *
 *   EMAIL.<SF|DF|CDF>.<EVENT>[.SECTION.<TRUE|FALSE>][.FUEL]   event is the 3rd segment
 *   DASHBOARD.<PAGE>[...][.FUEL]                              event is the 2nd
 *   DASHBOARD.<FUEL>                                          NO event segment at all
 *   HER                                                       no event, no fuel
 *
 * That third shape is why this exists: reading "the segment after the product" turns
 * `DASHBOARD.ELECTRIC` into the event "ELECTRIC", so the UI labels a dashboard row with its own
 * fuel type. That bug was fixed separately in the compare table, the import picker and the
 * backend's displayName derivation before being pulled into one place here.
 */

export const FUEL_SEGMENTS = ['ELECTRIC', 'GAS', 'WATER'] as const;
const SUBTYPE_SEGMENTS = ['SF', 'DF', 'CDF'];

const segments = (featureId: string): string[] => (featureId || '').split('.').filter(Boolean);

/** The fuel a featureId belongs to, or null when it is fuel-agnostic (5-part EMAIL/CDF, HER). */
export const featureIdFuel = (featureId: string): string | null => {
  const last = segments(featureId).pop()?.toUpperCase() ?? '';
  return (FUEL_SEGMENTS as readonly string[]).includes(last) ? last : null;
};

/**
 * The event a featureId names — never a fuel type.
 *
 * Falls back to the product when the id carries no event of its own (`DASHBOARD.ELECTRIC`),
 * since there the product IS the unit being configured.
 */
export const featureIdEvent = (featureId: string): string => {
  const parts = segments(featureId).map((p) => p.toUpperCase());
  if (parts.length === 0) return '';
  const [product, second] = parts;

  // EMAIL-style: a sub-brand token sits between the product and the event.
  if (second && SUBTYPE_SEGMENTS.includes(second)) return parts[2] || product;

  // Everything else (DASHBOARD/WEB): the event is the 2nd segment — unless that segment is the
  // fuel, which means there is no event and the product stands in for it.
  if (!second || (FUEL_SEGMENTS as readonly string[]).includes(second)) return product;
  return second;
};

/**
 * The unit an import selects on — stable across LEVEL_1 and LEVEL_2, which the event is not.
 *
 * EMAIL keys on its event: the same segment appears at both levels
 * (`EMAIL.DF.BILL_PROJECTION.ELECTRIC` / `EMAIL.DF.BILL_PROJECTION.FOOTER.FALSE.ELECTRIC`).
 * DASHBOARD can't: L1 is bare `DASHBOARD.ELECTRIC` while L2 carries a page
 * (`DASHBOARD.ENERGY_INSIGHTS.ELECTRIC`), so keying on the event would never match across
 * levels and the dashboard would look un-importable. The product is its unit.
 */
export const featureIdImportUnit = (featureId: string): string => {
  const product = segments(featureId)[0]?.toUpperCase() ?? '';
  if (product === 'DASHBOARD' || product === 'WEB') return product;
  return featureIdEvent(featureId);
};

/** "BILL_PROJECTION" -> "Bill Projection". */
export const titleCaseSegment = (segment: string): string =>
  segment
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/** Human label for a featureId, derived from its event: "Bill Projection", "Dashboard". */
export const featureIdLabel = (featureId: string): string => titleCaseSegment(featureIdEvent(featureId));

/**
 * Label for a usage-mode DASHBOARD row, whose API displayName is only its period ("Month"),
 * collapsing genuinely different rows onto one name.
 *
 *   DASHBOARD.ENERGY_INSIGHTS_USAGE_MODE_PAGE.USAGE.APPLIANCE_VIEW.MONTH.ELECTRIC
 *                                             └─────── detail ───────┘
 *
 * "Usage" always leads; the OTHER of the two leading segments is the view, whichever position
 * it sits in. So `USAGE.APPLIANCE_VIEW` reads "Usage · Appliance View" and `COST.USAGE_VIEW`
 * reads "Usage · Cost View" — not a positional rule, which is why the pair can't just be
 * title-cased in order.
 *
 * Only ids carrying a USAGE segment are rewritten. Everything else — `ENERGY_INSIGHTS_PAGE.
 * RESIDENTIAL.GB.ACTIVITY_MAP`, plain `ENERGY_INSIGHTS` — keeps the API's own displayName.
 */
const viewStem = (segment: string): string => segment.replace(/_VIEW$/, '');

/** The usage-row detail minus its leading "Usage" — `['Appliance View', 'Day']`. Null when the
 *  row isn't a usage-mode dashboard row. Lets a caller group under one "Usage" heading and drop
 *  the word from every row beneath it. */
export const dashboardUsageDetailParts = (featureId: string): string[] | null => {
  const parts = segments(featureId);
  const product = parts[0]?.toUpperCase();
  if (product !== 'DASHBOARD' && product !== 'WEB') return null;

  const end = featureIdFuel(featureId) ? parts.length - 1 : parts.length;
  const detail = parts.slice(2, end).map((p) => p.toUpperCase());
  if (detail.length < 2) return null;

  const usageAt = detail.findIndex((p) => viewStem(p) === 'USAGE');
  if (usageAt === -1) return null;

  const [view, ...period] = detail.filter((_, i) => i !== usageAt);
  return [`${titleCaseSegment(viewStem(view))} View`, ...period.map(titleCaseSegment)];
};

const dashboardUsageDetail = (featureId: string): string | null => {
  const parts = dashboardUsageDetailParts(featureId);
  return parts ? ['Usage', ...parts].join(' · ') : null;
};

const MANDATORY_SEGMENTS = ['TRUE', 'FALSE'];

/**
 * What distinguishes a LEVEL_2 id from its siblings — the section inside the event.
 *
 *   EMAIL.DF.BILL_PROJECTION.TEASER.TRUE.ELECTRIC   ->  ['TEASER']
 *   DASHBOARD.ENERGY_INSIGHTS_PAGE.ACTIVITY_MAP.GAS ->  ['ACTIVITY_MAP']
 *
 * Null for LEVEL_1 ids, which have nothing after the event — so a caller falling back through
 * this still gets the event for them. Without it every section under one event renders as that
 * event: nine rows all reading "Bill Projection".
 */
const featureIdDetailSegments = (featureId: string): string[] | null => {
  const parts = segments(featureId).map((p) => p.toUpperCase());
  let end = parts.length;
  if ((FUEL_SEGMENTS as readonly string[]).includes(parts[end - 1])) end -= 1;
  if (MANDATORY_SEGMENTS.includes(parts[end - 1])) end -= 1;

  // EMAIL puts a sub-brand token before the event, pushing the detail one segment later.
  const start = SUBTYPE_SEGMENTS.includes(parts[1]) ? 3 : 2;
  const detail = parts.slice(start, end);
  return detail.length > 0 ? detail : null;
};

/**
 * Label for a row that came with its own `displayName` from the API.
 *
 * Prefers the API's name, with two corrections: a usage-mode dashboard row gets its full
 * detail (above) rather than just its period, and a name that is merely the row's fuel is
 * rejected — the feature-metric transform derives LEVEL_1 display names from the last segment,
 * which for `DASHBOARD.ELECTRIC` is the fuel.
 *
 * With no name at all it derives one: the section detail for a LEVEL_2 id, the event for a
 * LEVEL_1 one.
 */
export const resolveFeatureDisplayName = (displayName: string | null | undefined, featureId: string): string => {
  const detail = dashboardUsageDetail(featureId);
  if (detail) return detail;
  const name = displayName?.trim();
  if (name && !(FUEL_SEGMENTS as readonly string[]).includes(name.toUpperCase())) return name;
  const section = featureIdDetailSegments(featureId);
  return section ? section.map(titleCaseSegment).join(' · ') : featureIdLabel(featureId);
};

/**
 * "Bill Projection (EL)" — a name suffixed with the fuel it belongs to.
 *
 * Needed wherever the same event appears once per fuel: without the suffix the rows/options are
 * indistinguishable, and `DASHBOARD.ELECTRIC` / `DASHBOARD.GAS` both read just "Dashboard".
 */
const FUEL_LABELS: Record<string, string> = { ELECTRIC: 'EL' };

export const withFuelLabel = (name: string, fuel: string | null): string =>
  fuel ? `${name} (${FUEL_LABELS[fuel] ?? fuel})` : name;
